/**
 * Centralized SEO configuration
 * Maps route paths to their SEO metadata (title, description, keywords, etc.)
 */

const baseUrl = 'https://www.vizbadger.com';

/**
 * SEO metadata configuration
 * Each entry maps a route path to its SEO metadata
 */
export const seoConfig = {
	'/': {
		title: 'VizBadger',
		description: 'Data-driven insights using analytics engineering, data pipelines, and simulations. Staff Analytics Engineer specializing in analytics schema design, data pipeline engineering, and scalable data infrastructure.',
		keywords: 'analytics engineering, data pipelines, simulations, sports analytics, predictive modeling, data visualization, analytics schemas, data infrastructure',
		canonical: `${baseUrl}/`
	},
	'/about': {
		title: 'About | VizBadger',
		description: 'Predict and report sports performance using analytics, machine learning and sports modelling.',
		keywords: 'Sports models, sports analytics, sports analysis, sports predictions, sports tip, predictive modelling, predictive sports modelling, sports data, sports prediction'
	},
	'/betting': {
		title: 'Sports Betting | VizBadger',
		description: 'Learn all about sports betting, accumulators and how to understand value bets. Improve your knowledge of the system and process to become a better sports bettor.',
		keywords: 'Sports betting, Betting accumulators, Betting exchanges, Betting value, Accumulators, Betting guide, Betting UK, Football betting, Betting accumulator strategy, Betting value strategy, Betting odds value, Betting value bets, Value betting strategy',
		canonical: `${baseUrl}/betting`
	},
	'/betting/understand-sports-betting': {
		title: 'Understand Sports Betting: Build Intuition and Avoid Losing | VizBadger',
		description: 'Learn all about sports betting, accumulators and how to understand value bets.',
		keywords: 'Sports betting, Betting accumulators, Betting exchanges, Betting value, Accumulators, Betting guide, Betting UK, Football betting, Betting accumulator strategy, Betting value strategy, Betting odds value, Betting value bets, Value betting strategy',
		canonical: `${baseUrl}/betting/understand-sports-betting`
	},
	'/betting/how-to-find-value-betting-on-sports': {
		title: 'How To Find Value Betting On Sports | VizBadger',
		description: 'Learn how to identify value bets in sports betting by understanding odds, probability, and expected value.',
		keywords: 'Value betting, Sports betting, Betting value, Expected value, Betting odds, Betting strategy'
	},
	'/betting/how-to-maximise-sports-betting-accumulators': {
		title: 'How To Maximise Sports Betting Accumulators | VizBadger',
		description: 'Learn strategies to maximize your sports betting accumulators and improve your betting success rate.',
		keywords: 'Betting accumulators, Accumulator betting, Sports betting, Betting strategy'
	},
	'/betting/sports-betting-bankroll-management-strategies': {
		title: 'Sports Betting Bankroll Management Strategies | VizBadger',
		description: 'Learn effective bankroll management strategies for sports betting to protect your funds and improve long-term profitability.',
		keywords: 'Bankroll management, Sports betting, Betting strategy, Money management, Betting bankroll'
	},
	'/betting/kelly-criterion-bankroll-management': {
		title: 'Kelly Criterion Bankroll Management | VizBadger',
		description: 'Learn about the Kelly Criterion for managing your sports betting bankroll and optimizing bet sizing.',
		keywords: 'Kelly Criterion, Bankroll management, Betting strategy, Optimal bet sizing'
	},
	'/sports-models': {
		title: 'Predictive Sports Modelling | VizBadger',
		description: 'Learn about predictive sports modeling, a method of using statistics and machine learning to predict sports outcomes. This page explains the concept and benefits, and provides an example of a Monte Carlo simulation system.',
		keywords: 'Sports models, sports analytics, sports analysis, sports predictions, sports tip, predictive modelling, predictive sports modelling, sports data, sports prediction, sports, statistics, machine learning, predictive modeling, Monte Carlo simulation, probabilistic approach, league winner',
		ogTitle: 'Predictive Sports Modelling | VizBadger',
		ogType: 'article',
		ogUrl: `${baseUrl}/sports-models`,
		ogDescription: 'Learn about predictive sports modeling, a method of using statistics and machine learning to predict sports outcomes. This page explains the concept and benefits, and provides an example of a Monte Carlo simulation system.'
	},
	'/sports-models/monte-carlo-simulation-sports-prediction': {
		title: 'Monte Carlo Simulations and Sports Predictions | VizBadger',
		description: 'Learn how Monte Carlo simulations can be used to predict sports outcomes and understand probabilistic modeling.',
		keywords: 'Monte Carlo simulation, Sports predictions, Probabilistic modeling, Sports analytics',
		canonical: `${baseUrl}/sports-models/monte-carlo-simulation-sports-prediction`
	},
	'/sports-models/sports-ranking-methods-colley-massey-elo': {
		title: 'Sports Ranking Methods: Colley, Massey, and ELO Ratings | VizBadger',
		description: 'Compare and understand different sports ranking methods including Colley, Massey, and ELO rating systems.',
		keywords: 'Sports ranking, Colley method, Massey method, ELO rating, Sports analytics'
	},
	'/sports-models/english-pool-monte-carlo-simulation': {
		title: 'English Pool Monte Carlo Simulation | VizBadger',
		description: 'Simulate and predict pool games using player shot accuracy and break efficiency. Adjust player skills and attributes to view the impact on results.',
		keywords: 'Pool simulation, Monte Carlo, Pool predictions, Sports simulation'
	},
	'/sports-models/golf': {
		title: 'How To Predict Golf Tournaments | VizBadger',
		description: 'Predict the outcome of a golf event using monte carlo simulations and modeling a round of golf programmatically.',
		keywords: 'Golf predictions, Golf modeling, Golf stats, Monte Carlo simulation, Golf analytics'
	},
	'/sports-models/golf/round-prediction': {
		title: 'How To Predict A Golf Round | VizBadger',
		description: 'Learn how to predict individual golf round scores using statistical modeling and Monte Carlo simulations.',
		keywords: 'Golf round prediction, Golf modeling, Golf statistics'
	},
	'/cricket': {
		title: 'Explore Test Match Batting Careers | VizBadger',
		description: 'Search and explore cricketers\' batting careers through visualisations and data analytics.',
		keywords: 'Cricket analytics, Cricket statistics, Test cricket, Batting careers'
	},
	'/data-visualisation/dynamic-d3-components': {
		title: 'Dynamic D3.js Graph | VizBadger',
		description: 'Interact with a dynamic graph that automatically scales to device size with D3.js.',
		keywords: 'D3.js, Data visualization, Interactive graphs, Data visualization library'
	},
	'/analytics/designing-analytics-schemas': {
		title: 'Designing Analytics Schemas | VizBadger',
		description: 'Learn how to design analytics schemas that enable impactful data-driven decision making. Discover the importance of a reliable and trustworthy data schema, and explore tips and considerations for building and maintaining one.',
		keywords: 'Data Engineering, Data Pipelines, Analytics, Data Analytics, Analytics engineering, Data-driven decision making, Data schema, Business intelligence, Data science projects, Analytics engineering principles, Self-serve systems, Source of truth, Slowly changing dimensions, Entity tables, Aggregate tables, KPI metrics, Business value, Reliable data, Trustworthy data'
	},
	'/steam/insights': {
		title: 'Steam Insights | VizBadger',
		description: 'Achievement completion over time, next-play recommendations and community unlock rates for my Steam library, rebuilt from dated events with layered SQL models instead of daily snapshots.',
		keywords: 'Steam achievements, Steam completion rate, Data modelling, dbt, Event-based history, Data pipelines'
	},
	'/duckdb': {
		title: 'DuckDB Monitoring | VizBadger',
		description: 'Real-time monitoring dashboard for DuckDB analytics database. View health status, table statistics, and refresh operation history.',
		keywords: 'DuckDB, Analytics database, Database monitoring, Data infrastructure'
	},
	'/pool/matches': {
		title: 'Amateur Pool Rankings | VizBadger',
		description: 'Use the Colley ranking system to calculate which amateur pool player is the best.',
		keywords: 'Pool rankings, Colley method, Pool statistics'
	},
	'/games': {
		title: 'Games | VizBadger',
		description: 'Interactive game simulations and analysis tools. Explore probability and statistics through playable games and Monte Carlo simulations.',
		keywords: 'Game simulations, Probability, Statistics, Monte Carlo, Interactive games'
	},
	'/games/snakes-and-ladders': {
		title: 'Snakes and Ladders Simulation | VizBadger',
		description: 'Play Snakes and Ladders or simulate thousands of games to analyze patterns. Explore how long games take and the impact of different starting positions.',
		keywords: 'Snakes and Ladders, Board game simulation, Probability, Game analysis, Monte Carlo simulation'
	},
	'/games/nonogram-solver': {
		title: 'Nonogram Row Solver | VizBadger',
		description: 'Solve nonogram picture-logic puzzle rows interactively. Enter a row length and clue numbers to see which cells are guaranteed filled or empty, with a step-by-step explanation of the deduction.',
		keywords: 'Nonogram solver, Picture logic puzzle, Griddler solver, Line solver, Puzzle logic, Nonogram clues'
	}
};

/**
 * Dynamic SEO pattern matchers
 * For routes with dynamic segments (e.g., /pool/group/:groupUrlSlug)
 */
const dynamicSEOPatterns = [
	{
		pattern: /^\/pool\/group\/([^/]+)$/,
		baseConfig: seoConfig['/pool/matches'],
		// Title and description can be customized per group via API data
		generateTitle: (matches) => `${matches[1]} Pool Rankings | VizBadger`,
		generateDescription: (matches) => `Pool rankings and statistics for ${matches[1]} group.`
	},
	{
		pattern: /^\/pool\/player\/(\d+)$/,
		baseConfig: seoConfig['/pool/matches'],
		generateTitle: (matches) => `Pool Player Profile | VizBadger`,
		generateDescription: (matches) => `Pool player statistics, match history, and rankings.`
	},
	{
		pattern: /^\/pool\/group\/([^/]+)\/ratings-explanation$/,
		baseConfig: seoConfig['/pool/matches'],
		generateTitle: (matches) => `Ratings Explanation - ${matches[1]} | VizBadger`,
		generateDescription: (matches) => `Explanation of the Colley ranking methodology for ${matches[1]} pool group.`
	},
	{
		pattern: /^\/pool\/predictions\/([^/]+)$/,
		baseConfig: seoConfig['/pool/matches'],
		generateTitle: (matches) => `Pool Predictions - ${matches[1]} | VizBadger`,
		generateDescription: (matches) => `Match predictions for ${matches[1]} pool group.`
	},
	{
		pattern: /^\/cricket\/player\/(.+)$/,
		baseConfig: seoConfig['/cricket'],
		generateTitle: (matches) => `${decodeURIComponent(matches[1])} - Test Match Career | VizBadger`,
		generateDescription: (matches) => `Test match batting career statistics for ${decodeURIComponent(matches[1])}.`
	}
];

/**
 * Get SEO metadata for a given pathname
 * Supports both static and dynamic routes
 * @param {string} pathname - The current pathname (e.g., '/betting/understand-sports-betting' or '/pool/group/hurricane')
 * @param {object} dynamicData - Optional dynamic data for SEO (e.g., group name, player name from API)
 * @returns {object|null} - SEO config object or null if not found
 */
export const getSEOForPath = (pathname, dynamicData = {}) => {
	// Normalize pathname
	const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
	
	// Check for exact match first (static routes)
	if (seoConfig[normalizedPath]) {
		return seoConfig[normalizedPath];
	}
	
	// Check dynamic patterns
	for (const { pattern, baseConfig, generateTitle, generateDescription } of dynamicSEOPatterns) {
		const matches = normalizedPath.match(pattern);
		if (matches) {
			return {
				...baseConfig,
				title: generateTitle ? generateTitle(matches, dynamicData) : baseConfig.title,
				description: generateDescription ? generateDescription(matches, dynamicData) : baseConfig.description,
				// Use dynamic data if provided (e.g., actual group name from API)
				...(dynamicData.title && { title: dynamicData.title }),
				...(dynamicData.description && { description: dynamicData.description })
			};
		}
	}
	
	// Check for path prefixes (most specific first) - for nested static routes
	const sortedPaths = Object.keys(seoConfig).sort((a, b) => b.length - a.length);
	for (const path of sortedPaths) {
		if (normalizedPath.startsWith(path)) {
			return seoConfig[path];
		}
	}
	
	// Return default/fallback SEO
	return {
		title: 'VizBadger',
		description: 'Data-driven insights using analytics engineering, data pipelines, and simulations.',
		keywords: 'analytics engineering, data pipelines, simulations, sports analytics'
	};
};

/**
 * Generate Helmet component props from SEO config
 * @param {string} pathname - The current pathname
 * @returns {object} - Props object for Helmet component
 */
export const getHelmetProps = (pathname) => {
	const seo = getSEOForPath(pathname);
	const props = {
		title: seo.title
	};
	
	if (seo.description) {
		props.meta = [
			{ name: 'description', content: seo.description },
			...(seo.keywords ? [{ name: 'keywords', content: seo.keywords }] : [])
		];
	}
	
	if (seo.canonical) {
		props.link = [{ rel: 'canonical', href: seo.canonical }];
	}
	
	// Add Open Graph tags if present
	if (seo.ogTitle || seo.ogDescription || seo.ogUrl || seo.ogType) {
		props.meta = [
			...props.meta,
			...(seo.ogTitle ? [{ property: 'og:title', content: seo.ogTitle }] : []),
			...(seo.ogType ? [{ property: 'og:type', content: seo.ogType }] : []),
			...(seo.ogUrl ? [{ property: 'og:url', content: seo.ogUrl }] : []),
			...(seo.ogDescription ? [{ property: 'og:description', content: seo.ogDescription }] : [])
		];
	}
	
	return props;
};

